import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import {
  AsyncEntityProvider,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';
import { EntityDetailsPage } from './EntityDetailsPage';

/**
 * Standalone route wrapper for the entity details page under /catalog/:namespace/:kind/:name.
 * Fetches the entity from the catalog by URL params and provides it via AsyncEntityProvider.
 */
export const EntityDetailsRoute = () => {
  const {
    namespace = 'default',
    kind = 'component',
    name = '',
  } = useParams<{ namespace: string; kind: string; name: string }>();

  const catalogApi = useApi(catalogApiRef);
  const [entity, setEntity] = useState<Entity | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  const fetchEntity = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await catalogApi.getEntityByRef(
        `${kind}:${namespace}/${name}`,
      );
      setEntity(result ?? undefined);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [catalogApi, namespace, kind, name]);

  useEffect(() => {
    fetchEntity();
  }, [fetchEntity]);

  return (
    <AsyncEntityProvider
      entity={entity}
      loading={loading}
      error={error}
      refresh={fetchEntity}
    >
      <EntityDetailsPage />
    </AsyncEntityProvider>
  );
};
